import React, { useState } from "react";
import { Mail, Send, MessageCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";

const CATEGORIES = ["registration", "login", "quiz", "technical", "result", "other"];

export default function Contact() {
  const { config } = useApp();
  const [form, setForm] = useState({ name: "", email: "", category: "general", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/support", form);
      toast.success("Support request received. We'll get back to you soon.");
      setForm({ name: "", email: "", category: "general", subject: "", message: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-12 md:py-16 grid md:grid-cols-2 gap-12" data-testid="contact-page">
      <div>
        <div className="text-xs font-bold tracking-[0.3em] text-[#FF9933] uppercase">We're Here To Help</div>
        <h1 className="text-4xl font-black font-heading tracking-tighter text-slate-900 mt-2">Contact Support</h1>
        <p className="text-slate-500 mt-3 text-sm leading-relaxed">Facing issues with registration, login, the quiz or your result? Send us a message.</p>
        <div className="mt-8 space-y-4">
          {config?.support_email && (
            <a href={`mailto:${config.support_email}`} className="flex items-center gap-3 text-sm font-semibold text-slate-700 hover:text-[#FF9933] transition-colors">
              <span className="w-10 h-10 rounded-lg bg-[#FF9933]/10 flex items-center justify-center"><Mail className="w-4 h-4 text-[#FF9933]" /></span>
              {config.support_email}
            </a>
          )}
          {config?.socials?.discord && (
            <a href={config.socials.discord} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-sm font-semibold text-slate-700 hover:text-[#003366] transition-colors">
              <span className="w-10 h-10 rounded-lg bg-[#003366]/10 flex items-center justify-center"><MessageCircle className="w-4 h-4 text-[#003366]" /></span>
              Join the LosTDRKC Discord
            </a>
          )}
        </div>
      </div>
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-xl space-y-4" data-testid="support-form">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Name</label>
            <Input data-testid="support-name" className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div>
            <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Email</label>
            <Input data-testid="support-email" type="email" className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          </div>
        </div>
        <div>
          <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Issue Type</label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="support-category" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Subject</label>
          <Input data-testid="support-subject" className="mt-1.5" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required />
        </div>
        <div>
          <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Message</label>
          <Textarea data-testid="support-message" className="mt-1.5" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required />
        </div>
        <button type="submit" disabled={busy} data-testid="support-submit" className="w-full py-3.5 rounded-lg bg-[#FF9933] text-white font-bold hover:bg-[#E6892E] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
          <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send Message"}
        </button>
      </form>
    </div>
  );
}
